import { writeDB, api } from './store';
import { Membership } from './membership';
import { history } from './history';
import { userPath, usersPath } from '../routes';
import md5 from 'md5';
import { GetParams, PaginatedLoaderResponse, paginatedLoaderResponseFrom } from './data';
import { BasePermissions } from './permission';
import { PaginatedResponse } from './api';
import { I18n } from '../locales/translate';

/* INTERFACES */
export interface Asset {
  id: string;
  url: string;
  tags: string[];
  thumbs?: Record<string, unknown>;
}

export interface UserBase {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  slug?: string;
  locale?: string;
}

export interface User extends UserBase {
  id: string;
  avatar?: Asset;
  memberships: Membership[];
  $permissions: BasePermissions[];
  createdAt?: Date;
}

export interface UserEdit extends UserBase {
  id?: string;
  avatar?: string;
}

/* USERS CREATE */
export async function addUser(user: UserEdit): Promise<void> {
  const response = await api.createUser(user);
  writeDB.table('users').insert(response.data);
  history.push(userPath(response.data as User));
}

/* USERS READ */
export async function getRawUsers({ page, perPage, term }: GetParams): Promise<PaginatedResponse<User[]>> {
  const response = await api.indexUsers({
    page: (page || 0) + 1,
    perPage,
    term,
  });
  return response as PaginatedResponse<User[]>;
}

export async function getUsers(page: number, perPage: number, term: string): Promise<PaginatedLoaderResponse> {
  const response = await api.indexUsers({
    page: (page || 0) + 1,
    perPage,
    term,
  });
  writeDB.table('users').upsert(response.data);
  return paginatedLoaderResponseFrom(response);
}

export async function getRawUser(userId: string): Promise<PaginatedResponse<User>> {
  const response = await api.showUser({ userId });
  return response as unknown as PaginatedResponse<User>;
}

export async function getUser(userId: string): Promise<void> {
  const resp = await api.showUser({ userId });
  writeDB.table('users').upsert(resp.data);
}

/* USERS UPDATE */
export async function patchUser(userId: string, user: UserEdit): Promise<void> {
  const response = await api.updateUser({
    userId,
    ...user,
  });
  // TODO: Update api model to match local interface
  const data = response.data as unknown as User;
  writeDB.table('users').update(userId, data);
}

/* USERS DELETE */
export async function deleteUser(userId: string): Promise<void> {
  await api.deleteUser({ userId });
  history.push(usersPath());
}

/* HELPERS */
export function fullUserName(
  user: User | undefined,
  { fallbackToEmail = false }: { fallbackToEmail?: boolean } = {}
): string {
  if (!user || (!user.firstName && !user.lastName)) {
    return (fallbackToEmail && user?.email) || '';
  }
  return [user.firstName, user.lastName].join(' ');
}

export function userDisplayLine(user: User): string {
  const email = user.email && `(${user.email})`;
  return [fullUserName(user), email].filter(Boolean).join(' ');
}

export function gravatar(email: string, { size }: { size: number } = { size: 80 }): string {
  return `https://www.gravatar.com/avatar/${md5(email || '')}.jpg?s=${size}&d=mp`;
}

export function userNameAndOrEmail(
  userName?: string,
  email?: string,
  fallback: string | null = I18n.translate.model.user._name.toLowerCase()
): string | null {
  if (userName === undefined && email === undefined) return fallback;

  const emailPart = email ? ` (${email})` : '';
  return (userName ? `${userName}${emailPart}` : email) || fallback;
}
